import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


import { adminService } from '../../services/adminService';
import UseCaseManagementPanel from './components/UseCaseManagementPanel';
import AIToolManagementPanel from './components/AIToolManagementPanel';
import RelationshipMatrix from './components/RelationshipMatrix';
import JSONImportPanel from './components/JSONImportPanel';

const UseCaseAIToolManagement = () => {
  const [useCases, setUseCases] = useState([]);
  const [aiTools, setAITools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('relationships');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [useCasesData, aiToolsData] = await Promise.all([
        adminService?.getUseCasesWithTools(),
        adminService?.getAIToolsWithInstructions()
      ]);
      setUseCases(useCasesData);
      setAITools(aiToolsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'relationships', label: 'Relationship Matrix', icon: 'Network' },
    { id: 'usecases', label: 'Use Cases', icon: 'Layers' },
    { id: 'aitools', label: 'AI Tools', icon: 'Cpu' },
    { id: 'import', label: 'JSON Import', icon: 'Upload' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading management interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">Use Case & AI Tool Management</h1>
          <p className="text-muted-foreground">
            Manage use cases, AI tools, and their relationships
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive mb-1">Error</h3>
                <p className="text-sm text-destructive/80">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadData}
                  className="mt-3"
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-border">
          <div className="flex gap-4">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary font-semibold' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-card border border-border rounded-lg">
          {activeTab === 'relationships' && (
            <RelationshipMatrix
              useCases={useCases}
              aiTools={aiTools}
              onRefresh={loadData}
            />
          )}
          {activeTab === 'usecases' && (
            <UseCaseManagementPanel
              useCases={useCases}
              onRefresh={loadData}
            />
          )}
          {activeTab === 'aitools' && (
            <AIToolManagementPanel
              aiTools={aiTools}
              onRefresh={loadData}
            />
          )}
          {activeTab === 'import' && (
            <JSONImportPanel
              onRefresh={loadData}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UseCaseAIToolManagement;